import React, { useMemo, useState } from "react";
import {
  Navbar,
  Nav,
  Container,
  Row,
  Col,
  Button,
  Form,
  Card,
  Badge,
  Accordion,
  Modal,
  ProgressBar,
  Alert,
  OverlayTrigger,
  Tooltip,
} from "react-bootstrap";
import {
  Check2Circle,
  ShieldLock,
  GraphUpArrow,
  PersonBadge,
  StarFill,
  QuestionCircle,
  Heartbreak,
  People,
  JournalCheck,
} from "react-bootstrap-icons";

// -----------------------------
// DivorceReadyScoreCard.com - Landing Page
// React + TypeScript + React-Bootstrap + react-bootstrap-icons
// -----------------------------

// ---- Types ----
interface LeadForm {
  fullName: string;
  email: string;
  state: string;
  dependents: number | ""; // children or dependents involved
  monthlyIncome: number | "";
  monthlyExpenses: number | "";
  creditScore: number | ""; // 300-850
  emergencyFund: number | ""; // cash on hand
  conflictLevel: number | ""; // 1-5
  docsReady: boolean; // key documents gathered
  triedCounseling: boolean; // attempted counseling/mediation
  consent: boolean;
}

interface ScoreResult {
  score: number; // 0 - 100
  grade: "A" | "B" | "C" | "D" | "F";
  headline: string;
  suggestions: string[];
}

const statesUS = [
  "AL","AK","AZ","AR","CA","CO","CT","DE","FL","GA","HI","ID","IL","IN","IA","KS","KY","LA","ME","MD","MA","MI","MN","MS","MO","MT","NE","NV","NH","NJ","NM","NY","NC","ND","OH","OK","OR","PA","RI","SC","SD","TN","TX","UT","VT","VA","WA","WV","WI","WY"
];

// ---- Helpers ----
const clamp = (n: number, min: number, max: number) => Math.max(min, Math.min(max, n));

function computeScore(input: LeadForm): ScoreResult {
  // Defensive parsing
  const income = typeof input.monthlyIncome === "number" ? Math.max(0, input.monthlyIncome) : 0;
  const expenses = typeof input.monthlyExpenses === "number" ? Math.max(0, input.monthlyExpenses) : 0;
  const net = Math.max(0, income - expenses);
  const credit = typeof input.creditScore === "number" ? clamp(input.creditScore, 300, 850) : 600;
  const fund = typeof input.emergencyFund === "number" ? Math.max(0, input.emergencyFund) : 0;
  const conflict = typeof input.conflictLevel === "number" ? clamp(input.conflictLevel, 1, 5) : 3;
  const dependents = typeof input.dependents === "number" ? clamp(input.dependents, 0, 10) : 0;

  // Transparent demo model (not legal advice):
  // Financial capacity 45% (net cash flow 25, emergency fund 20)
  // Administrative readiness 25% (documents ready 15, organization/credit 10)
  // Complexity dampeners 30% (conflict & dependents reduce score)
  const cashFlowComponent = Math.min(25, (net / 5000) * 25); // $5k+ surplus maxes out
  const fundComponent = Math.min(20, (fund / 10000) * 20); // $10k+ maxes out
  const docsComponent = input.docsReady ? 15 : 0;
  const creditComponent = ((credit - 300) / (850 - 300)) * 10; // 0..10

  // Complexity: higher conflict and more dependents reduce readiness
  const conflictPenalty = ((conflict - 1) / 4) * 15; // 0..15
  const dependentsPenalty = Math.min(15, dependents * 3); // up to 5 children -> 15

  let raw = cashFlowComponent + fundComponent + docsComponent + creditComponent;
  raw = raw - conflictPenalty - dependentsPenalty;

  // Counseling/mediation attempt slightly improves outlook
  if (input.triedCounseling) raw += 5;

  const score = Math.round(clamp(raw, 0, 100));
  const grade: ScoreResult["grade"] = score >= 90 ? "A" : score >= 75 ? "B" : score >= 60 ? "C" : score >= 45 ? "D" : "F";

  const headlineMap: Record<ScoreResult["grade"], string> = {
    A: "You’re Ready to Proceed—Well Prepared",
    B: "You’re Close—Solid Foundation",
    C: "Almost There—Tighten Key Areas",
    D: "Lay the Groundwork First",
    F: "Start with Essentials—Build Stability",
  };

  const suggestions: string[] = [];
  if (net < 1000) suggestions.push("Increase monthly surplus—reduce expenses or grow income to create a buffer.");
  if (fund < 3000) suggestions.push("Build an emergency fund ($3k–$10k) to handle transition costs.");
  if (!input.docsReady) suggestions.push("Gather key documents: tax returns (2y), bank statements (3–6m), pay stubs, asset/liability list.");
  if (credit < 680) suggestions.push("Improve credit: pay down revolving balances and correct report errors.");
  if (conflict >= 4) suggestions.push("Consider mediation or a cooling‑off plan to reduce conflict level.");
  if ((dependents as number) >= 1) suggestions.push("Prepare a co‑parenting plan outline and schedules for dependents.");
  if (suggestions.length === 0) suggestions.push("Consult a qualified attorney/mediator to finalize your plan.");

  return { score, grade, headline: headlineMap[grade], suggestions };
}

// ---- Small bits ----
const HeroBadge: React.FC<{ text: string }> = ({ text }) => (
  <Badge bg="warning" pill className="ms-2 align-middle text-dark">{text}</Badge>
);

const Feature: React.FC<{ icon: React.ReactNode; title: string; text: string }> = ({ icon, title, text }) => (
  <Card className="h-100 shadow-sm border-0">
    <Card.Body>
      <div className="d-flex align-items-center mb-3" style={{ gap: 12 }}>
        <div className="bg-light rounded-circle d-flex align-items-center justify-content-center" style={{ width: 44, height: 44 }}>
          {icon}
        </div>
        <Card.Title className="mb-0 fw-semibold">{title}</Card.Title>
      </div>
      <Card.Text className="text-secondary">{text}</Card.Text>
    </Card.Body>
  </Card>
);

const TestimonialCard: React.FC<{ name: string; quote: string; stars?: number }> = ({ name, quote, stars = 5 }) => (
  <Card className="h-100 border-0 shadow-sm">
    <Card.Body>
      <div className="mb-2" aria-label={`${stars} star rating`}>
        {Array.from({ length: stars }).map((_, i) => <StarFill key={i} className="me-1" />)}
      </div>
      <blockquote className="blockquote mb-0">
        <p className="fs-6">“{quote}”</p>
        <footer className="blockquote-footer mt-2">{name}</footer>
      </blockquote>
    </Card.Body>
  </Card>
);

const FooterLink: React.FC<{ href: string; children: React.ReactNode }> = ({ href, children }) => (
  <a href={href} className="text-decoration-none text-secondary">{children}</a>
);

const scrollToId = (id: string) => {
  const el = document.getElementById(id);
  if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
};

// ---- Main Component ----
const DivorceReadyScoreCardLandingPage_2025_11_07_chat: React.FC = () => {
  const [showModal, setShowModal] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [lead, setLead] = useState<LeadForm>({
    fullName: "",
    email: "",
    state: "CO",
    dependents: 0,
    monthlyIncome: "",
    monthlyExpenses: "",
    creditScore: "",
    emergencyFund: "",
    conflictLevel: 3,
    docsReady: false,
    triedCounseling: false,
    consent: false,
  });

  const result = useMemo(() => computeScore(lead), [lead]);
  const progressVariant = result.score >= 75 ? "success" : result.score >= 60 ? "info" : result.score >= 45 ? "warning" : "danger";

  // ---- Handlers (type-safe for react-bootstrap) ----
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, type, checked, value } = e.currentTarget;
    setLead((prev) => ({
      ...prev,
      [name]: type === "checkbox"
        ? checked
        : ["dependents","monthlyIncome","monthlyExpenses","creditScore","emergencyFund","conflictLevel"].includes(name)
          ? (value === "" ? "" : Number(value))
          : value,
    }));
  };

  const handleSelectChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const { name, value } = e.currentTarget;
    setLead((prev) => ({ ...prev, [name]: value }));
  };

  const formIsValid =
    lead.fullName.trim().length > 1 &&
    /.+@.+\..+/.test(lead.email) &&
    !!lead.state &&
    lead.consent;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitted(true);
    if (!formIsValid) return;
    setShowModal(true);
    // In production: POST to API (API Gateway/Lambda) with the lead + result
  };

  return (
    <div className="bg-white">
      {/* Top Nav */}
      <Navbar expand="lg" sticky="top" bg="white" className="shadow-sm">
        <Container>
          <Navbar.Brand href="#" className="fw-bold">
            Divorce<strong className="text-danger">Ready</strong>ScoreCard<small className="text-secondary">.com</small>
            <HeroBadge text="Free" />
          </Navbar.Brand>
          <Navbar.Toggle aria-controls="main-nav" />
          <Navbar.Collapse id="main-nav">
            <Nav className="ms-auto" navbarScroll>
              <Nav.Link onClick={() => scrollToId("how")}>How it works</Nav.Link>
              <Nav.Link onClick={() => scrollToId("features")}>Features</Nav.Link>
              <Nav.Link onClick={() => scrollToId("faqs")}>FAQs</Nav.Link>
              <Nav.Link onClick={() => scrollToId("get-started")}>Get Started</Nav.Link>
            </Nav>
          </Navbar.Collapse>
        </Container>
      </Navbar>

      {/* Hero */}
      <section className="py-5 py-lg-6 bg-light" id="hero">
        <Container>
          <Row className="align-items-center g-4">
            <Col lg={6}>
              <h1 className="display-5 fw-bold">
                A clear, private way to gauge your <span className="text-danger">Divorce‑Ready</span> status.
              </h1>
              <p className="lead text-secondary mt-3">
                Understand your financial and administrative readiness, reduce surprises, and plan next steps with confidence.
              </p>
              <div className="d-flex flex-wrap align-items-center gap-3 mt-3">
                <Button size="lg" onClick={() => scrollToId("get-started")}>
                  Get My Free Score
                </Button>
                <OverlayTrigger placement="bottom" overlay={<Tooltip>We keep your data private. You control it.</Tooltip>}>
                  <div className="d-flex align-items-center text-secondary">
                    <ShieldLock className="me-2" /> Privacy‑first
                  </div>
                </OverlayTrigger>
              </div>
              <div className="mt-4 d-flex align-items-center text-secondary" style={{ gap: 12 }}>
                <Check2Circle className="text-success" /> No legal advice — educational tool only.
              </div>
            </Col>
            <Col lg={6}>
              <Card className="shadow border-0">
                <Card.Body>
                  <div className="d-flex align-items-center mb-2">
                    <GraphUpArrow className="me-2" />
                    <h5 className="mb-0">Preview: Your Score</h5>
                    <Badge bg={progressVariant} className="ms-2">{result.grade}</Badge>
                  </div>
                  <ProgressBar now={result.score} variant={progressVariant} className="mb-2" />
                  <div className="small text-secondary">Live estimate updates as you type.</div>
                  <hr />
                  <Form noValidate onSubmit={handleSubmit} id="get-started">
                    <Row className="g-3">
                      <Col md={6}>
                        <Form.Group controlId="fullName">
                          <Form.Label>Full name</Form.Label>
                          <Form.Control
                            name="fullName"
                            value={lead.fullName}
                            onChange={handleInputChange}
                            placeholder="Taylor Smith"
                            isInvalid={submitted && lead.fullName.trim().length < 2}
                            required
                          />
                          <Form.Control.Feedback type="invalid">Please enter your name.</Form.Control.Feedback>
                        </Form.Group>
                      </Col>
                      <Col md={6}>
                        <Form.Group controlId="email">
                          <Form.Label>Email</Form.Label>
                          <Form.Control
                            name="email"
                            type="email"
                            value={lead.email}
                            onChange={handleInputChange}
                            placeholder="taylor@example.com"
                            isInvalid={submitted && !/.+@.+\..+/.test(lead.email)}
                            required
                          />
                          <Form.Control.Feedback type="invalid">Enter a valid email.</Form.Control.Feedback>
                        </Form.Group>
                      </Col>

                      <Col md={4}>
                        <Form.Group controlId="state">
                          <Form.Label>State</Form.Label>
                          <Form.Select name="state" value={lead.state} onChange={handleSelectChange} required>
                            {statesUS.map((s) => <option key={s} value={s}>{s}</option>)}
                          </Form.Select>
                        </Form.Group>
                      </Col>
                      <Col md={4}>
                        <Form.Group controlId="dependents">
                          <Form.Label>Dependents</Form.Label>
                          <Form.Control name="dependents" type="number" min={0} max={10} value={lead.dependents} onChange={handleInputChange} />
                        </Form.Group>
                      </Col>
                      <Col md={4}>
                        <Form.Group controlId="conflictLevel">
                          <Form.Label>Conflict level (1–5)</Form.Label>
                          <Form.Control name="conflictLevel" type="number" min={1} max={5} value={lead.conflictLevel} onChange={handleInputChange} />
                        </Form.Group>
                      </Col>

                      <Col md={6}>
                        <Form.Group controlId="monthlyIncome">
                          <Form.Label>Monthly income ($)</Form.Label>
                          <Form.Control name="monthlyIncome" type="number" min={0} value={lead.monthlyIncome} onChange={handleInputChange} placeholder="6000" />
                        </Form.Group>
                      </Col>
                      <Col md={6}>
                        <Form.Group controlId="monthlyExpenses">
                          <Form.Label>Monthly expenses ($)</Form.Label>
                          <Form.Control name="monthlyExpenses" type="number" min={0} value={lead.monthlyExpenses} onChange={handleInputChange} placeholder="4200" />
                        </Form.Group>
                      </Col>

                      <Col md={6}>
                        <Form.Group controlId="creditScore">
                          <Form.Label>Credit score (estimate)</Form.Label>
                          <Form.Control name="creditScore" type="number" min={300} max={850} value={lead.creditScore} onChange={handleInputChange} placeholder="700" />
                        </Form.Group>
                      </Col>
                      <Col md={6}>
                        <Form.Group controlId="emergencyFund">
                          <Form.Label>Emergency fund ($)</Form.Label>
                          <Form.Control name="emergencyFund" type="number" min={0} value={lead.emergencyFund} onChange={handleInputChange} placeholder="5000" />
                        </Form.Group>
                      </Col>

                      <Col md={6}>
                        <Form.Check
                          id="docsReady"
                          name="docsReady"
                          checked={lead.docsReady}
                          onChange={handleInputChange}
                          label={<span>Key documents gathered (tax returns, statements, pay stubs)</span>}
                        />
                      </Col>
                      <Col md={6}>
                        <Form.Check
                          id="triedCounseling"
                          name="triedCounseling"
                          checked={lead.triedCounseling}
                          onChange={handleInputChange}
                          label={<span>Tried counseling or mediation</span>}
                        />
                      </Col>

                      <Col xs={12}>
                        <Form.Check
                          id="consent"
                          name="consent"
                          checked={lead.consent}
                          onChange={handleInputChange}
                          label={<span>I agree to the <a href="#">Terms</a> and <a href="#">Privacy</a>.</span>}
                          required
                          isInvalid={submitted && !lead.consent}
                        />
                      </Col>

                      <Col xs={12} className="d-grid d-md-flex gap-3">
                        <Button type="submit" size="lg" className="px-4">Calculate My Score</Button>
                        <Button variant="outline-secondary" size="lg" onClick={() => scrollToId("how")}>How it works</Button>
                      </Col>
                    </Row>
                  </Form>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Container>
      </section>

      {/* Social proof */}
      <section className="py-4">
        <Container>
          <Row className="text-center text-secondary small g-4 align-items-center justify-content-center">
            <Col xs="auto"><PersonBadge className="me-1" /> Private & confidential — your data stays yours.</Col>
            <Col xs="auto"><JournalCheck className="me-1" /> Export checklist or share with a professional.</Col>
          </Row>
        </Container>
      </section>

      {/* Features */}
      <section className="py-5" id="features">
        <Container>
          <Row className="mb-4">
            <Col lg={8}>
              <h2 className="fw-bold">Clarity without pressure</h2>
              <p className="text-secondary mb-0">A readiness snapshot plus practical next steps you can take at your own pace.</p>
            </Col>
          </Row>
          <Row className="g-4">
            <Col md={6} lg={4}><Feature icon={<GraphUpArrow />} title="Instant Score" text="0–100 score with A–F grade—see where you stand right now." /></Col>
            <Col md={6} lg={4}><Feature icon={<ShieldLock />} title="Privacy‑First" text="No account required to try. No selling of personal data." /></Col>
            <Col md={6} lg={4}><Feature icon={<People />} title="Plan‑Ready" text="Create a checklist you can share with mediators or attorneys." /></Col>
          </Row>
        </Container>
      </section>

      {/* How it works */}
      <section className="py-5 bg-light" id="how">
        <Container>
          <Row className="mb-4">
            <Col lg={8}>
              <h2 className="fw-bold">How it works</h2>
              <p className="text-secondary">Three quick steps to clarity.</p>
            </Col>
          </Row>
          <Row className="g-4 align-items-stretch">
            <Col md={4}>
              <Card className="h-100 border-0 shadow-sm">
                <Card.Body>
                  <Badge bg="danger">Step 1</Badge>
                  <Card.Title className="mt-2">Enter a few details</Card.Title>
                  <Card.Text className="text-secondary">Income, expenses, credit estimate, dependents, and readiness items.</Card.Text>
                </Card.Body>
              </Card>
            </Col>
            <Col md={4}>
              <Card className="h-100 border-0 shadow-sm">
                <Card.Body>
                  <Badge bg="danger">Step 2</Badge>
                  <Card.Title className="mt-2">Get your score</Card.Title>
                  <Card.Text className="text-secondary">Transparent factors—finance, documentation, complexity—rolled into one view.</Card.Text>
                </Card.Body>
              </Card>
            </Col>
            <Col md={4}>
              <Card className="h-100 border-0 shadow-sm">
                <Card.Body>
                  <Badge bg="danger">Step 3</Badge>
                  <Card.Title className="mt-2">Follow the checklist</Card.Title>
                  <Card.Text className="text-secondary">Actionable steps to improve readiness or prepare for mediation.</Card.Text>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Container>
      </section>

      {/* Testimonials */}
      <section className="py-5">
        <Container>
          <Row className="mb-4">
            <Col lg={8}>
              <h2 className="fw-bold">What people say</h2>
              <p className="text-secondary">Early users on how the score helped them plan next steps.</p>
            </Col>
          </Row>
          <Row className="g-4">
            <Col md={6} lg={4}><TestimonialCard name="Avery R." quote="The checklist kept me focused and lowered my stress." /></Col>
            <Col md={6} lg={4}><TestimonialCard name="Jordan K." quote="Seeing the math on cash flow changed my timeline for the better." /></Col>
            <Col md={6} lg={4}><TestimonialCard name="Morgan L." quote="Loved that I could try it without creating an account." /></Col>
          </Row>
        </Container>
      </section>

      {/* FAQs */}
      <section className="py-5" id="faqs">
        <Container>
          <Row className="mb-4">
            <Col lg={8}>
              <h2 className="fw-bold">Frequently asked questions</h2>
              <p className="text-secondary">Short, helpful answers to common questions.</p>
            </Col>
          </Row>
          <Row>
            <Col lg={10}>
              <Accordion defaultActiveKey="0">
                <Accordion.Item eventKey="0">
                  <Accordion.Header><QuestionCircle className="me-2" /> Is this legal advice?</Accordion.Header>
                  <Accordion.Body>
                    No. This is an educational planning tool. For advice about your situation, consult a qualified attorney or mediator.
                  </Accordion.Body>
                </Accordion.Item>
                <Accordion.Item eventKey="1">
                  <Accordion.Header><QuestionCircle className="me-2" /> Will my information be shared?</Accordion.Header>
                  <Accordion.Body>
                    We value privacy. Your inputs remain private and you control export or deletion.
                  </Accordion.Body>
                </Accordion.Item>
                <Accordion.Item eventKey="2">
                  <Accordion.Header><QuestionCircle className="me-2" /> Does the score work in every state?</Accordion.Header>
                  <Accordion.Body>
                    The score is a universal readiness model. It’s not a substitute for state‑specific legal guidance.
                  </Accordion.Body>
                </Accordion.Item>
              </Accordion>
            </Col>
          </Row>
        </Container>
      </section>

      {/* Callout */}
      <section className="py-5 bg-danger text-white" id="contact">
        <Container>
          <Row className="align-items-center g-4">
            <Col lg={8}>
              <h2 className="fw-bold d-flex align-items-center" style={{ gap: 10 }}>
                <Heartbreak />
                Ready to take the next step?
              </h2>
              <p className="mb-0">Use your score to discuss options with a professional you trust.</p>
            </Col>
            <Col lg={4} className="d-grid d-lg-flex justify-content-lg-end gap-3">
              <Button variant="light" onClick={() => scrollToId("get-started")}>Calculate my score</Button>
              <Button variant="outline-light" onClick={() => window.location.assign("mailto:hello@DivorceReadyScoreCard.com")}>Email us</Button>
            </Col>
          </Row>
        </Container>
      </section>

      {/* Footer */}
      <footer className="py-4 border-top" id="footer">
        <Container>
          <Row className="align-items-center gy-3">
            <Col md={7}>
              <div className="fw-bold">DivorceReadyScoreCard.com</div>
              <div className="text-secondary small">© {new Date().getFullYear()} — Educational only. Not legal advice.</div>
            </Col>
            <Col md={5} className="d-flex justify-content-md-end gap-4">
              <FooterLink href="#">Privacy</FooterLink>
              <FooterLink href="#">Terms</FooterLink>
              <FooterLink href="#">Support</FooterLink>
            </Col>
          </Row>
        </Container>
      </footer>

      {/* Result Modal */}
      <Modal show={showModal} onHide={() => setShowModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Your Divorce‑Ready Score</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Alert variant={progressVariant as any} className="d-flex align-items-center justify-content-between">
            <div>
              <div className="fw-bold fs-5">{result.headline}</div>
              <div className="small text-secondary">Score</div>
            </div>
            <div className="display-6 fw-bold mb-0">{result.score}</div>
          </Alert>
          <ul className="small mb-0">
            {result.suggestions.map((s, i) => (
              <li key={i}>{s}</li>
            ))}
          </ul>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowModal(false)}>Close</Button>
          <Button onClick={() => {
            setShowModal(false);
            window.setTimeout(() => scrollToId("faqs"), 100);
          }}>See FAQs</Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default DivorceReadyScoreCardLandingPage_2025_11_07_chat;
